import { NavigateFunction } from "react-router-dom";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { FleetRowTransactions, Row, RowTransactions } from "./types";
import React from "react";
import { Tag } from "antd";
import i18n from "i18next";
import { Transactions as TypeTransactionsPage } from "pages/transactions/Transactions";
import { Fleets } from "../Fleets";
import { FleetTransactions } from "../Fleet";

type Sort = {
	totalEnergyKWh: boolean,
	totalSessions: boolean,
};

type SortTransactions = {
	startTime: boolean,
	endTime: boolean,
	length: boolean,
	energy: boolean,
};

type Transactions = {
	startTime: boolean,
	endTime: boolean,
	energy: boolean,
	revenue: boolean,
};

export const computeRowsAndCols = (
	data: Fleets,
	navigate: NavigateFunction,
	handleSort:(value: string) => void,
	shouldReset: (value: boolean) => void,
	reset: boolean,
	isAsc: Sort,
	isDesc: Sort,
	setIsAsc: (values: () =>Sort) => void,
	setIsDesc: (values: () =>Sort) => void) => {

	if (!data.length) {
		return {
			columns: [],
			rows: []
		};
	}

	const handleFilterChange = (field: string) => {
		if (!isAsc[field]) {
			setIsAsc(() => {
				const setSort = { totalEnergyKWh: false, totalSessions: false };
				setSort[field] = true;
				return { ...setSort };
			});
			handleSort(field + "_asc");
			setIsDesc(() => {
				return { totalEnergyKWh: false, totalSessions: false };
			});
		} else if (!isDesc[field] && isAsc[field]) {
			setIsAsc(() => {
				return { totalEnergyKWh: false, totalSessions: false };
			});
			setIsDesc(() => {
				const setSort = { totalEnergyKWh: false, totalSessions: false };
				setSort[field] = true;
				return { ...setSort };
			});
			handleSort(field + "_desc");
			shouldReset(false);
		}
	};
	
	const columns = [
		{
			title: <div className="table-header">{i18n.t("Vehicle ID")}</div>,
			dataIndex: "vehicleId",
			key: "vehicleId",
			align: "center" as const,
			render: (id) => {
        let index = data.findIndex((obj) => obj.vehicleId === id)
				if (data[index]) {
					return <a onClick={() =>
						navigate(`${data[index].vehicleId}`, {
							state: {
								status: data[index].status
							}
						})}
					>
						{data[index].vehicleId}
					</a>;
				}
				return <a>{data[index].vehicleId}</a>;
			},
		},
		{
			title: <div className="table-header">{i18n.t("Status")}</div>,
			key: "status",
			dataIndex: "status",
			align: "center" as const,
			render: (tag) =>
			{
				let color = "#CECECE";
				if (tag === "Plugged-in") {
					color = "#9DCC78";
				}
				return <Tag color={color} key={tag}>{tag.toUpperCase()}</Tag>;
			}
		},
		{
			title: <div className="table-header">{i18n.t("Charger ID")}</div>,
			dataIndex: "chargerId",
			key: "chargerId",
			align: "center" as const,
		},
		{
			title: <div className="table-header">{i18n.t("Charging Progress")}</div>,
			dataIndex: "chargingProgress",
			key: "chargingProgress",
			align: "center" as const,
		},
		{
			title: <div className="table-header">{i18n.t("Session Energy")}</div>,
			dataIndex: "sessionEnergyInKWh",
			key: "sessionEnergyInKWh",
			align: "center" as const,
			render: (_, tag) => {
				return <div>{tag.sessionEnergyInKWh == "N/A" ? tag.sessionEnergyInKWh : tag.sessionEnergyInKWh + 'KWh'}</div>;
			}
		},
		{
			title: <div className="table-header">{i18n.t("Session Length")}</div>,
			dataIndex: "sessionLengthMin",
			key: "sessionLengthMin",
			align: "center" as const,
			render: (_, tag) => {
				return <div>{tag.sessionLengthMin == "N/A" ? tag.sessionLengthMin : tag.sessionLengthMin + 'Min'}</div>;
			}
		},
		{
			title: <div className="table-header">{i18n.t("Total Energy")} {reset ?
				<CaretDownOutlined onClick={() => {
					handleFilterChange("totalEnergyKWh");
				}}/> : (!isDesc.totalEnergyKWh  ?
					<CaretDownOutlined onClick={() => {
						handleFilterChange("totalEnergyKWh");
					}}/> :
					<CaretUpOutlined onClick={() => {
						handleFilterChange("totalEnergyKWh");
					}} />)}</div>,
			dataIndex: "totalEnergyInKWh",
			key: "totalEnergyInKWh",
			align: "center" as const,
			render: (_, tag) => {
				return <div>{tag.totalEnergyInKWh == "N/A" ? tag.totalEnergyInKWh : tag.totalEnergyInKWh + 'KWh'}</div>;
			}
		},
		{
			title: <div className="table-header">{i18n.t("Total Sessions")} {reset ?
				<CaretDownOutlined onClick={() => {
					handleFilterChange("totalSessions");
				}}/> : (!isDesc.totalSessions  ?
					<CaretDownOutlined onClick={() => {
						handleFilterChange("totalSessions");
					}}/> :
					<CaretUpOutlined onClick={() => {
						handleFilterChange("totalSessions");
					}} />)}</div>,
			dataIndex: "totalSessions",
			key: "totalSessions",
			align: "center" as const,
		},
	];

	const rows:Row[] = [];
	Object.keys(data).forEach((key, index) => {
		const values = data[key];
		rows.push({
			key: (rows.length + 1).toString(),
			vehicleId: values.vehicleId,
      status:values.status,
			chargerId: values.chargerId,
			chargingProgress: values.chargingProgress,
      totalEnergyInKWh: values.totalEnergyInKWh,
      totalSessions: values.totalSessions,
      sessionEnergyInKWh: values.sessionEnergyInKWh,
      sessionLengthMin: values.sessionLengthMin,
		});
	});

	return {
		columns: columns,
		rows: rows
	};
};

export const computeRowsAndColsForTransactions = (
	data: FleetTransactions,
	) => {

	if (!data.length) {
		return {
			columns: [],
			rows: []
		};
	}

	const columns = [
		{
			title: <div className="table-header">{i18n.t("transactionId")}</div>,
			dataIndex: "transactionId",
			key: "transactionId",
			align: "center" as const,
		},
		{
			title: <div className="table-header">{i18n.t("chargerId")}</div>,
			dataIndex: "chargerId",
			key: "chargerId",
			align: "center" as const,
		},
		{
			title: <div className="table-header">{i18n.t("startTime")}</div>,
			dataIndex: "startTime",
			key: "startTime",
			align: "center" as const,
		},
		{
			title: <div className="table-header">{i18n.t("endTime")}</div>,
			key: "endTime",
			dataIndex: "endTime",
			align: "center" as const,
		},
		{
			title: <div className="table-header">{i18n.t("length")}</div>,
			dataIndex: "length",
			key: "length",
			align: "center" as const,
		},
		{
			title: <div className="table-header">{i18n.t("energy")}</div>,
			dataIndex: "energy",
			key: "energy",
			align: "center" as const,
		}
	];

	const rows:FleetRowTransactions[] = [];
	Object.keys(data).forEach(key => {
		const values = data[key];
		rows.push({
			key: (rows.length + 1).toString(),
			transactionId: values.transactionId,
			chargerId: values.chargerId,
			startTime: values.startTime,
			endTime: values.endTime,
			length: (values.sessionLength).toString() + " mins",
			energy: (values.energy).toString() + " " + values.energyUnit,
		});
	});

	return {
		columns: columns,
		rows: rows
	};
};

export const addZerosToStringNumber = (number: string) => {
	const arrNumber = number.split("");
	while(arrNumber.length < 4) {
		arrNumber.unshift("0");
	}
	return arrNumber.reduce((previous, current) => previous + current, "");
};
